import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, switchMap } from 'rxjs';
import { OidcSecurityService } from 'angular-auth-oidc-client';

export interface UserTeam {
  id: number;
  name: string;
  materializedPath: string;
}

export interface InProgressDashboardResponse {
  userTeams: UserTeam[];
  columns: {
    new: DashboardColumn;
    inReview: DashboardColumn;
    backlog: DashboardColumn;
    activeJdi: DashboardColumn;
    activeRieProject: DashboardColumn;
    activeTasks: DashboardColumn;
  };
}

export interface DashboardColumn {
  title: string;
  opportunities?: OpportunityCard[];
  tasks?: TaskCard[];
  count: number;
}

export interface OpportunityCard {
  id: number;
  title: string;
  opportunityType: 'jdi' | 'rie_kaizen' | 'project';
  score?: number;
  problemStatement: string;
  primaryImprovementCategory?: string;
  backlogReason?: string[];
  inReviewReason?: string[];
  assignee?: {
    id: number;
    firstName: string;
    lastName: string;
    profileImageUrl?: string;
  };
  teamMembers: {
    id: number;
    firstName: string;
    lastName: string;
    profileImageUrl?: string;
  }[];
  tasksCount: {
    completed: number;
    total: number;
  };
  attachmentsCount: number;
  agingStatus: {
    days: number;
    type: 'since_created' | 'until_due';
    isOverdue?: boolean;
    isWarning?: boolean;
  };
  teamName: string;
  fullTeamName: string;
  status: 'new' | 'in_review' | 'backlog' | 'active' | 'declined' | 'complete' | 'cancelled';
  createdAt: Date;
  dueDate?: Date;
}

export interface TaskCard {
  id: number;
  description: string;
  status: 'incomplete' | 'complete';
  dueDate?: Date;
  assignee?: {
    id: number;
    firstName: string;
    lastName: string;
    profileImageUrl?: string;
  };
  opportunityId?: number;
  opportunityTitle?: string;
}

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  private apiUrl = `${window.location.origin}/api/dashboards`;

  constructor(
    private http: HttpClient,
    private oidcSecurityService: OidcSecurityService
  ) {}

  getUserTeams(): Observable<UserTeam[]> {
    return this.oidcSecurityService.getIdToken().pipe(
      switchMap((idToken) =>
        this.http.get<UserTeam[]>(`${this.apiUrl}/teams`, {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        })
      )
    );
  }

  getInProgressDashboardData(
    teamIds?: number[],
    myAssignmentsOnly?: boolean,
    searchText?: string
  ): Observable<InProgressDashboardResponse> {
    return this.oidcSecurityService.getIdToken().pipe(
      switchMap((idToken) => {
        let params = new HttpParams();
        
        // Always send teamIds when defined, even if empty, to distinguish between "no filter" and "filter by empty set"
        if (teamIds !== undefined) {
          if (teamIds.length === 0) {
            // Send empty array as a special marker
            params = params.set('teamIds', '');
          } else {
            teamIds.forEach(teamId => {
              params = params.append('teamIds', teamId.toString());
            });
          }
        }
        
        if (myAssignmentsOnly) {
          params = params.set('myAssignmentsOnly', 'true');
        }
        
        if (searchText) {
          params = params.set('searchText', searchText);
        }

        return this.http.get<InProgressDashboardResponse>(`${this.apiUrl}/in-progress-board`, {
          params,
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        });
      })
    );
  }
}